import { Link } from 'react-router-dom';
import { useProgress } from '../../hooks/useProgress';
import SEOHead from '../../components/feature/SEOHead';

interface Course {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  lessons: number;
  available: boolean;
  route: string;
}

export default function FreeLessonsHub() {
  const { getCourseProgress } = useProgress();

  const courses: Course[] = [
    {
      id: 'algebra1',
      title: 'Algebra 1',
      description: 'Master the fundamentals of algebra with equations, inequalities, functions, and more',
      icon: '📐',
      color: 'from-blue-500 to-indigo-600',
      lessons: 12,
      available: true,
      route: '/free-lessons/algebra1'
    },
    {
      id: 'geometry',
      title: 'Geometry',
      description: 'Explore angles, triangles, the Pythagorean theorem, and geometric proofs',
      icon: '📏',
      color: 'from-green-500 to-emerald-600',
      lessons: 6,
      available: true,
      route: '/free-lessons/geometry'
    },
    {
      id: 'algebra2',
      title: 'Algebra 2 & Trigonometry',
      description: 'Advanced algebra topics including quadratics, exponentials, and logarithms',
      icon: '📊',
      color: 'from-purple-500 to-pink-600',
      lessons: 6,
      available: true,
      route: '/free-lessons/algebra2'
    },
    {
      id: 'precalc',
      title: 'Pre-Calculus & Calculus',
      description: 'Dive into limits, derivatives, integrals, and polynomial functions',
      icon: '∫',
      color: 'from-orange-500 to-red-600',
      lessons: 8,
      available: false,
      route: '#'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      <SEOHead
        title="Free Math Lessons Online | Algebra, Geometry & More | Math Mastery"
        description="Access free math lessons covering Algebra 1, Geometry, and Algebra 2 with Mr. Augustine's exclusive short methods. 20+ practice questions per topic with step-by-step solutions. No login required."
        keywords="free math lessons, free algebra lessons, free geometry lessons, online math lessons, math practice questions"
        canonicalPath="/free-lessons"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Free Math Lessons',
          description: 'Free online math lessons covering Algebra 1, Geometry, and Algebra 2.',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Algebra 1 Free Lessons', url: '/free-lessons/algebra1' },
            { '@type': 'ListItem', position: 2, name: 'Geometry Free Lessons', url: '/free-lessons/geometry' },
            { '@type': 'ListItem', position: 3, name: 'Algebra 2 Free Lessons', url: '/free-lessons/algebra2' },
          ],
        }}
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-600 to-amber-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors">
            <i className="ri-arrow-left-line"></i>
            <span className="font-medium">Back to Home</span>
          </Link>
          <h1 className="text-6xl font-bold mb-6">🎓 Free Math Lessons</h1>
          <p className="text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Learn at your own pace with Mr. Augustine's comprehensive lessons, complete with textbook explanations, exclusive short methods, and practice questions
          </p>
        </div>
      </div>

      {/* Courses Grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-8">
          {courses.map((course) => {
            const progress = getCourseProgress(course.id, course.lessons);
            
            return (
              <Link
                key={course.id}
                to={course.available ? course.route : '#'}
                className={`group relative bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 ${
                  course.available 
                    ? 'hover:shadow-2xl hover:-translate-y-1 cursor-pointer' 
                    : 'opacity-60 cursor-not-allowed'
                }`}
              >
                {/* Course Header */}
                <div className={`bg-gradient-to-r ${course.color} p-8 text-white relative`}>
                  {!course.available && (
                    <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold">
                      Coming Soon
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-4xl">
                      {course.icon}
                    </div>
                    <div className="flex-1">
                      <h2 className="text-3xl font-bold">{course.title}</h2>
                      <p className="text-white/90 mt-1">{course.lessons} comprehensive lessons</p>
                    </div>
                  </div>
                  
                  <p className="text-white/90 text-lg leading-relaxed">{course.description}</p>
                </div>

                {/* Progress Section */}
                {course.available && (
                  <div className="p-6 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700">Your Progress</span>
                      <span className="text-lg font-bold text-gray-900">{progress.percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                      <div 
                        className={`bg-gradient-to-r ${course.color} rounded-full h-3 transition-all duration-500`}
                        style={{ width: `${progress.percentage}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-600">
                      {progress.completed} of {progress.total} lessons complete
                    </p>
                  </div>
                )}

                {/* Course Content */}
                <div className="p-8">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-book-open-line text-2xl text-orange-500 mt-1"></i>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-1">Textbook Explanations</h3>
                        <p className="text-gray-600 text-sm">Complete theory with formulas and theorems</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <i className="ri-lightbulb-flash-line text-2xl text-purple-500 mt-1"></i>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-1">Mr. Augustine's Short Methods</h3>
                        <p className="text-gray-600 text-sm">Exclusive tricks to solve problems faster</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <i className="ri-pencil-line text-2xl text-green-500 mt-1"></i>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-1">Practice Questions</h3>
                        <p className="text-gray-600 text-sm">20+ problems per lesson with step-by-step solutions</p>
                      </div>
                    </div>
                  </div>

                  {course.available && (
                    <div className="mt-6 flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border-2 border-orange-200">
                      <span className="font-semibold text-gray-900">Start Learning</span>
                      <i className="ri-arrow-right-line text-2xl text-orange-600 group-hover:translate-x-1 transition-transform"></i>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">Why Choose Our Free Lessons?</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-star-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Teaching</h3>
              <p className="text-gray-600">Learn from Mr. Augustine's 15+ years of teaching experience</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Learn at Your Pace</h3>
              <p className="text-gray-600">No deadlines, no pressure — study whenever you want</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-trophy-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Track Progress</h3>
              <p className="text-gray-600">Mark lessons complete and see how far you've come</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}